import { computed, reactive, toRaw } from 'vue'
import { defineStore } from 'pinia'
import type { StoreApi } from 'zustand/vanilla'
import { setIn, deleteIn, type Path } from './paths'

type Selectors<S> = Record<string, (state: S) => unknown>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Actions = Record<string, (...args: any[]) => unknown>

function isContainer(v: unknown): v is object {
  return v !== null && typeof v === 'object'
}

/**
 * The Vue face of a Zustand store — one way in, one way out.
 *
 * Reads: a deep `reactive` mirror of the Zustand state, refreshed on every
 * `setState` by assigning each top-level key. Components track it exactly as
 * they tracked Pinia state.
 *
 * Writes: everything the shell hands out is a forwarding Proxy over the mirror.
 * `uiStore.modals.scenarios = false` — including the 39 `v-model` bindings on
 * store paths — becomes `store.setState(setIn(state, ['modals','scenarios'], false))`.
 * Zustand's state is never mutated in place, so React subscribers see every change.
 *
 * Getters are selectors over the mirror (reactive computeds); actions are the
 * module functions of the Zustand store, passed through.
 */
export function mirrorStore<S extends object>(
  id: string,
  store: StoreApi<S>,
  extras: { selectors?: Selectors<S>; actions?: Actions } = {},
) {
  return defineStore(id, () => {
    const mirror = reactive({ ...store.getState() }) as Record<string, unknown>
    store.subscribe((next) => {
      const nextRecord = next as Record<string, unknown>
      for (const key of Object.keys(nextRecord)) mirror[key] = nextRecord[key]
      for (const key of Object.keys(mirror)) if (!(key in nextRecord)) delete mirror[key]
    })

    function forwarding(target: object, path: Path): object {
      return new Proxy(target, {
        get(t, key, receiver) {
          // `toRaw()` asks for this, and a forwarding proxy that answered by
          // forwarding handed back another forwarding proxy — so `toRaw` never
          // reached the plain object. Two real consequences: a consumer could
          // not get a stable identity out of the mirror (the network rows came
          // back as a new proxy per read), and the `set` trap below would write
          // a Proxy into Zustand state.
          if (key === '__v_raw') return toRaw(t)
          const value = Reflect.get(t, key, receiver)
          if (typeof key === 'symbol') return value
          return isContainer(value) ? forwarding(value, [...path, key]) : value
        },
        set(_t, key, value) {
          if (typeof key === 'symbol') return false
          store.setState(setIn(store.getState(), [...path, key], toRaw(value)) as S, true)
          return true
        },
        deleteProperty(_t, key) {
          if (typeof key === 'symbol') return false
          store.setState(deleteIn(store.getState(), [...path, key]) as S, true)
          return true
        },
      })
    }
    const root = forwarding(mirror, []) as Record<string, unknown>

    const exposed: Record<string, unknown> = {}
    for (const key of Object.keys(store.getState())) {
      exposed[key] = computed({
        get: () => root[key],
        set: (value: unknown) => {
          store.setState({ [key]: toRaw(value) } as Partial<S>)
        },
      })
    }
    for (const [name, select] of Object.entries(extras.selectors ?? {})) {
      exposed[name] = computed(() => select(mirror as unknown as S))
    }
    for (const [name, action] of Object.entries(extras.actions ?? {})) {
      exposed[name] = action
    }
    return exposed
  })
}
